import React, { useMemo } from "react";
import { useForm, useFormState } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { makeStyles, Typography } from "@material-ui/core";
import clsx from "clsx";
import InfoIcon from "../../../../../components/InfoIcon";
import Button from "../../../../../components/Button";
import theme from "../../../../../themes";
import { useWizard } from "../../../../wizard/context";
import { WizardMode } from "../../../../wizard/types";
import { IIntakeFront } from "../../../types";
import { useEditIntakeConfig } from "../../hooks/useEditIntakeConfig";
import { IntakeItem } from "./IntakeItem";

const useStyles = makeStyles(() => ({
  mainTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#535358",
    marginTop: 22,
  },
  existingIntakesContainer: {
    marginTop: 24,
  },
  addNewButton: {
    fontSize: 17,
    fontWeight: "bold",
    height: 44,
    color: "#fefefe",
    backgroundColor: "#7ec884",
    width: 156,
    paddingLeft: "32px !important",
    paddingRight: "32px !important"
  },
  addNewButtonContainer: {
    textAlign: "right",
    marginBottom: 24,
  },
  fieldArrayContainer: {
    display: "flex",
    flexDirection: "column-reverse",
  },
  asterisk: {
    color: "#535358",
    lineHeight: "inherit",
    fontSize: 16,
    fontWeight: "normal",
    marginLeft: 3,
  },
  hasError: {
    color: theme.palette.error.main,
  },
}));

export const Intakes = ({ stepName }) => {
  const classes = useStyles();

  const formData = useFormState();
  const {
    change,
    mutators: { push, remove },
  } = useForm();

  const { openWizard } = useWizard();
  const { getDeleteIntakeConfig, getDeactivateConfig } = useEditIntakeConfig();

  const { submitFailed, dirtySinceLastSubmit, invalid } = formData;
  const hasFormError = useMemo(
    () => invalid && submitFailed && !dirtySinceLastSubmit,
    [invalid, dirtySinceLastSubmit, submitFailed],
  );
  const intakeErrors =
    formData.submitErrors?.[stepName];
  const hasError =
    intakeErrors?.intake ||
    intakeErrors?.existingIntakes.length ||
    intakeErrors?.defaultNewIntakes.length ||
    intakeErrors?.newIntakes.length;

  const activeStepData = formData.values?.[stepName];
  const mode = formData.values?.mode;

  const recoverExistingIntake = (intakeIndex) =>
    change(`${stepName}.existingIntakes.${intakeIndex}.isDeleted`, false);
  const deleteExistingIntake = async (
    { isActive, approved, pending }: IIntakeFront,
    intakeIndex: number,
  ) => {
    if (isActive && +approved + +pending > 0) {
      await openWizard(getDeleteIntakeConfig({ approved, pending }));
    } else {
      change(`${stepName}.existingIntakes.${intakeIndex}.isDeleted`, true);
    }
  };
  const changeExistingIntakeState = async (
    { isActive, approved, pending }: IIntakeFront,
    intakeIndex: number,
  ) => {
    const existingIntakeState =
      activeStepData.existingIntakes[intakeIndex].isActive;

    if (isActive && +approved + +pending > 0) {
      const result = await openWizard(
        getDeactivateConfig({ approved, pending }),
      );

      if (result) {
        change(`${stepName}.existingIntakes.${intakeIndex}.isActive`, false);
        change(`${stepName}.existingIntakes.${intakeIndex}.deactivationNote`, {
          text: result.reason,
        });
      }
    } else {
      change(
        `${stepName}.existingIntakes.${intakeIndex}.isActive`,
        !existingIntakeState,
      );
    }
  };

  const deleteNewIntake = (intakeIndex) =>
    remove(`${stepName}.newIntakes`, intakeIndex);
  const changeNewIntakeState = (index) => {
    const newIntakeState = activeStepData?.newIntakes?.[index]?.isActive ?? true;
    change(`${stepName}.newIntakes.${index}.isActive`, !newIntakeState);
  };

  const isDefaultNewIntakeVisible =
    mode === WizardMode.Create && !activeStepData?.isDefaultNewIntakeDeleted;
  const defaultNewIntakeState =
    activeStepData?.defaultNewIntakes?.[0]?.isActive ?? true;
  const changeDefaultNewIntakeState = () =>
    change(`${stepName}.defaultNewIntakes.0.isActive`, !defaultNewIntakeState);
  const deleteDefaultNewIntake = () => {
    change(`${stepName}.defaultNewIntakes`, []);
    change(
      `${stepName}.isDefaultNewIntakeDeleted`,
      true,
    );
  };

  const isLastIntake = useMemo(() => {
    const newIntakesLength = activeStepData?.newIntakes?.length || 0;

    if (mode === WizardMode.Create) {
      const defaultNewIntakesLength = activeStepData?.isDefaultNewIntakeDeleted
        ? 0
        : 1;

      return newIntakesLength + defaultNewIntakesLength === 1;
    } else if (mode === WizardMode.Edit) {
      const notDeletedIntakesLength = (
        activeStepData?.existingIntakes || []
      ).filter((intake) => intake.isDeleted !== true).length;

      return notDeletedIntakesLength + newIntakesLength === 1;
    }
  }, [activeStepData]);

  return (
    <div>
      <div className={classes.mainTitle}>
        Intake
        <Typography
          className={clsx(
            classes.asterisk,
            hasFormError && hasError && classes.hasError,
          )}
          component={"span"}
          variant={"subtitle2"}
        >
          *
        </Typography>
        <InfoIcon text="You need to indicate the number of intakes. Minimum 1 intake is required." />
      </div>
      <div className={classes.existingIntakesContainer}>
        <FieldArray name={`${stepName}.existingIntakes`}>
          {({ fields }) =>
            fields.map((name, index) => (
              <IntakeItem
                key={index}
                name={name}
                mode={mode}
                startDate={activeStepData.existingIntakes[index].startDate}
                deadline={activeStepData.existingIntakes[index].deadline}
                state={activeStepData.existingIntakes[index].isActive}
                onStateChange={() =>
                  changeExistingIntakeState(
                    activeStepData.existingIntakes[index],
                    index,
                  )
                }
                approvedAppCount={activeStepData.existingIntakes[index].approved}
                pendingAppCount={activeStepData.existingIntakes[index].pending}
                onDelete={() =>
                  deleteExistingIntake(
                    activeStepData.existingIntakes[index],
                    index,
                  )
                }
                recoverIntakeHandler={() => recoverExistingIntake(index)}
                isIntakeDeleted={
                  activeStepData.existingIntakes[index]?.isDeleted
                }
                isDeleteDisabled={isLastIntake}
                isStartDateDisabled={true}
              />
            ))
          }
        </FieldArray>
      </div>
      <div className={classes.addNewButtonContainer}>
        <Button
          color={"success"}
          fullWidth
          onClick={() => push(`${stepName}.newIntakes`, undefined)}
          variant="contained"
          className={classes.addNewButton}
        >
          Add Intake
        </Button>
      </div>
      <div className={classes.fieldArrayContainer}>
        <FieldArray name={`${stepName}.newIntakes`}>
          {({ fields }) =>
            fields.map((name, index) => (
              <IntakeItem
                key={index}
                name={name}
                mode={mode}
                startDate={activeStepData?.newIntakes?.[index]?.startDate}
                deadline={activeStepData?.newIntakes?.[index]?.deadline}
                state={activeStepData?.newIntakes?.[index]?.isActive ?? true}
                onStateChange={() => changeNewIntakeState(index)}
                onDelete={() => deleteNewIntake(index)}
                isDeleteDisabled={isLastIntake}
              />
            ))
          }
        </FieldArray>
      </div>
      {isDefaultNewIntakeVisible && (
        <IntakeItem
          name={`${stepName}.defaultNewIntakes.0`}
          mode={mode}
          startDate={activeStepData?.defaultNewIntakes?.[0]?.startDate}
          deadline={activeStepData?.defaultNewIntakes?.[0]?.deadline}
          state={defaultNewIntakeState}
          onStateChange={changeDefaultNewIntakeState}
          onDelete={deleteDefaultNewIntake}
          isDeleteDisabled={isLastIntake}
        />
      )}
    </div>
  );
};
